import * as React from "react";
import { AxiosError } from "axios";
import { NavigateFunction } from "react-router-dom";
import { createApi } from "../../../../utils/createApi";
import { universalAPIErrorHandler } from "../../../../utils/universalAPIErrorHandler";
import { Dispatcher, WireGuardTunnel } from "./siteAboutInterfaces";
import { fetchWireguardTunnelsForCustomer } from "./fetchWireguardTunnels";
import { WireGuardTab } from "../WireGuardTab";
import { useSiteAboutStore } from "../siteAboutStore/siteAboutStore";
import { WireGuardEditSession } from "../WireGuardTabEditMode";

export const handlePatchWireguardTunnels = async (
  session: WireGuardEditSession | null,
  customerId: string,
  setWireguardTunnels: Dispatcher<WireGuardTunnel[]>,
  setWireguardChangesMade: Dispatcher<boolean>,
  setEditMode: Dispatcher<boolean>,
  setActiveTab: Dispatcher<React.ReactElement>,
  setBackdropLoadingScreenOn: Dispatcher<boolean>,
  navigate: NavigateFunction,
  selectedWireguardTunnelId: number | null,
  setSelectedWireguardTunnelId: React.Dispatch<
    React.SetStateAction<number | null>
  >
): Promise<void> => {
  const AxiosUI = createApi("");
  const { setScheduleGridEditable } = useSiteAboutStore.getState();

  const retry = () => {
    handlePatchWireguardTunnels(
      session,
      customerId,
      setWireguardTunnels,
      setWireguardChangesMade,
      setEditMode,
      setActiveTab,
      setBackdropLoadingScreenOn,
      navigate,
      selectedWireguardTunnelId,
      setSelectedWireguardTunnelId
    );
  };

  if (!session?.snapshot?.length || !session.drafts || !customerId) {
    setBackdropLoadingScreenOn(false);
    return;
  }

  const { snapshot: wireguardTunnelsEditSnapshot, drafts: wireguardDraftsById } =
    session;

  const dirtyIds = wireguardTunnelsEditSnapshot
    .filter((snap) => {
      const d = wireguardDraftsById[snap.id];
      if (!d) {
        return false;
      }
      return (
        d.name !== snap.name || d.client_public_key !== snap.client_public_key
      );
    })
    .map((t) => t.id);

  if (dirtyIds.length === 0) {
    setWireguardChangesMade(false);
    setScheduleGridEditable(false);
    setEditMode(false);
    setActiveTab(
      <WireGuardTab
        wireguardTunnels={wireguardTunnelsEditSnapshot}
        wireguardTunnelsLoading={false}
        selectedTunnelId={selectedWireguardTunnelId}
        onSelectTunnelId={(id) => setSelectedWireguardTunnelId(id)}
      />
    );
    setBackdropLoadingScreenOn(false);
    return;
  }

  try {
    for (const id of dirtyIds) {
      const draft = wireguardDraftsById[id];
      await AxiosUI.patch(`wireguard_tunnel/${id}/`, {
        name: draft.name,
        client_public_key: draft.client_public_key,
      });
    }

    const fresh = await fetchWireguardTunnelsForCustomer(customerId);
    setWireguardTunnels(fresh);
    setWireguardChangesMade(false);
    setScheduleGridEditable(false);
    setEditMode(false);

    const nextSelected =
      selectedWireguardTunnelId != null &&
      fresh.some((t) => t.id === selectedWireguardTunnelId)
        ? selectedWireguardTunnelId
        : fresh[0]?.id ?? null;
    if (nextSelected !== selectedWireguardTunnelId) {
      setSelectedWireguardTunnelId(nextSelected);
    }

    setActiveTab(
      <WireGuardTab
        wireguardTunnels={fresh}
        wireguardTunnelsLoading={false}
        selectedTunnelId={nextSelected}
        onSelectTunnelId={(id) => setSelectedWireguardTunnelId(id)}
      />
    );
    setBackdropLoadingScreenOn(false);
  } catch (reason) {
    universalAPIErrorHandler(reason as AxiosError, navigate, retry, () => {
      setWireguardChangesMade(false);
      setEditMode(false);
      setActiveTab(
        <WireGuardTab
          wireguardTunnels={wireguardTunnelsEditSnapshot}
          wireguardTunnelsLoading={false}
          selectedTunnelId={selectedWireguardTunnelId}
          onSelectTunnelId={(id) => setSelectedWireguardTunnelId(id)}
        />
      );
      setBackdropLoadingScreenOn(false);
    });
  }
};
